import { useCallback, useEffect, useRef, useState } from "react";

// One cache per mounted tenant/session/source. Pages commit atomically and retries are idempotent.
export function useSessionPages(
  api,
  clientSlug,
  method,
  sessionId,
  budget = 10,
) {
  const cache = useRef({
    pages: [],
    next: undefined,
    done: false,
    snapshot: null,
    seen: new Set(),
  });
  const generation = useRef(0);
  const [state, setState] = useState({
    pages: [],
    loading: true,
    error: null,
    done: false,
  });
  const publish = useCallback(
    (extra = {}) =>
      setState({
        pages: [...cache.current.pages],
        done: cache.current.done,
        loading: false,
        error: null,
        ...extra,
      }),
    [],
  );
  const run = useCallback(
    async (reset = false) => {
      const version = ++generation.current;
      if (reset)
        cache.current = {
          pages: [],
          next: undefined,
          done: false,
          snapshot: null,
          seen: new Set(),
        };
      publish({ loading: true });
      try {
        for (let i = 0; i < budget && !cache.current.done; i++) {
          const offsetMode =
            method === "getAgentConversationHistory" ||
            method === "getAgentConversation";
          const next = cache.current.next;
          const pageKey = next === undefined ? "first" : String(next);
          const data = await api[method]({
            ...(sessionId ? { sessionId } : {}),
            ...(offsetMode ? { offset: next ?? 0 } : { cursor: next }),
          });
          if (generation.current !== version) return;
          if (
            data.clientSlug !== clientSlug ||
            (sessionId && data.sessionId !== sessionId)
          )
            throw new Error("Workspace response mismatch");
          if (data.available === false) throw new Error("Source unavailable");
          if (
            data.coverage?.asOf &&
            cache.current.snapshot &&
            data.coverage.asOf !== cache.current.snapshot
          ) {
            cache.current = {
              pages: [],
              next: undefined,
              done: false,
              snapshot: null,
              seen: new Set(),
            };
            throw new Error("Snapshot changed. Retry to load fresh history.");
          }
          cache.current.snapshot ||= data.coverage?.asOf;
          const following = offsetMode ? data.nextOffset : data.nextCursor;
          if (
            following != null &&
            (String(following) === pageKey ||
              cache.current.seen.has(String(following)))
          )
            throw new Error("Pagination did not advance");
          if (!cache.current.seen.has(pageKey)) {
            cache.current.pages.push(data);
            cache.current.seen.add(pageKey);
          }
          cache.current.next = following;
          cache.current.done = following == null;
          publish({ loading: !cache.current.done && i < budget - 1 });
        }
        if (generation.current === version) publish();
      } catch (error) {
        if (generation.current !== version) return;
        if (/cursor/i.test(error.code || ""))
          cache.current = {
            pages: [],
            next: undefined,
            done: false,
            snapshot: null,
            seen: new Set(),
          };
        publish({ error });
      }
    },
    [api, clientSlug, method, sessionId, budget, publish],
  );
  const invalidate = useCallback(() => {
    generation.current++;
  }, []);
  useEffect(() => {
    run(true);
    return invalidate;
  }, [run, invalidate]);
  return {
    ...state,
    load: () => run(false),
    refresh: () => run(true),
    cancel: () => {
      generation.current++;
      publish();
    },
  };
}
